import { useState } from "react";

import { AsciiMoon } from "@/components/AsciiMoon";
import { ClientSetup } from "@/components/ClientSetup";
import { OnboardPill } from "@/components/OnboardPill";
import { Divider, GUTTER, PageShell } from "@/components/SiteChrome";
import { Sticker } from "@/components/Sticker";
import { RocketSticker, StarSticker } from "@/components/stickers";
import { FAQ } from "@/content/faq";
import { partnerCode } from "@/lib/partner";
import { cn } from "@/lib/utils";

// DESIGN.md: Landing page sequence — hero, setup, FAQ.

/** The MCP address to show: a partner's (/mcp/p/<code>) when their link set the cookie. */
const mcpUrl = () => {
  const partner = partnerCode();
  return `${location.origin}/mcp${partner ? `/p/${partner}` : ""}`;
};

function Hero() {
  return (
    <section aria-labelledby="hero-title" className="grid md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
      <div className={cn(GUTTER, "py-zsd-section flex flex-col justify-center min-w-0")}>
        <Sticker tilt="l" className="self-start mb-zsd-xl">
          your keys stay in your wallet
        </Sticker>
        <h1
          id="hero-title"
          className="m-0 mb-zsd-lg font-zsd-display-xl text-zsd-headline-lg lg:text-zsd-display-xl text-balance"
        >
          Launch tokens from your AI
        </h1>
        {/* Bottom margin leaves room for the pill's tooltip. */}
        <p className="m-0 mb-zsd-3xl font-zsd-body-lg text-zsd-body-lg text-zsd-muted max-w-[34em]">
          Give your agent one prompt to connect Moonlauncher, then ask it to launch a token. You get the token and its
          own website, and you sign in your own wallet.
        </p>
        <OnboardPill origin={location.origin} />
      </div>
      <div className="relative grid place-items-center p-zsd-xl pt-0 md:pt-zsd-xl min-w-0">
        <AsciiMoon className="w-full" />
        <RocketSticker size={56} className="absolute top-zsd-xl right-zsd-xl tilt-r" />
        <StarSticker size={24} className="absolute bottom-zsd-xl left-zsd-xl tilt-l" />
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section
      id="faq"
      aria-labelledby="faq-title"
      className={cn(GUTTER, "py-zsd-section grid md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-zsd-xl md:gap-zsd-3xl")}
    >
      <div>
        <h2 id="faq-title" className="m-0 font-zsd-headline-lg text-zsd-headline-lg">
          Questions
        </h2>
        <Sticker tilt="r" className="mt-zsd-lg">
          ser, read this
        </Sticker>
      </div>
      <div className="line-t">
        {FAQ.map((item) => (
          <details key={item.q} className="group line-b">
            <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-start justify-between gap-zsd-lg py-zsd-lg font-zsd-body-lg text-zsd-body-lg hover:text-zsd-primary ease">
              {item.q}
              <span
                aria-hidden="true"
                className="ease inline-block font-zsd-label-lg text-zsd-headline-md leading-none text-zsd-primary group-open:rotate-45"
              >
                +
              </span>
            </summary>
            <p className="m-0 pb-zsd-xl pr-zsd-3xl text-zsd-muted">{item.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

export function Landing() {
  const [client, setClient] = useState(0);
  return (
    <PageShell width="full">
      <Hero />
      <Divider label="[ 01 / 02 ] · Setup" />
      <ClientSetup url={mcpUrl()} selected={client} onSelect={setClient} />
      <Divider label="[ 02 / 02 ] · Questions" />
      <Faq />
    </PageShell>
  );
}
